import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save, FolderTree, ListTree, PackageOpen, FileSpreadsheet, FileText, ImagePlus, GanttChartSquare, Gauge, Lock, Unlock, CopyPlus, TrendingUp, Truck, PanelRightClose, PanelRightOpen, Search } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Toolbar, ToolGroup, Panel, SummaryBar, Badge, EmptyState } from "@/components/ui/exec";

const uid = () => Math.random().toString(36).slice(2, 10);
const blank = () => ({
  nombre: "", cliente: "",
  params: { indirectos_pct: 0, financiamiento_pct: 0, utilidad_pct: 0, cargos_sfp_pct: 0 },
  moneda_base: "MXN", tipo_cambio: { USD: 0, EUR: 0 },
  nodes: [],
  programa: { fecha_inicio: "", actividades: [] },
  mineria: { toneladas: 0, metros_desarrollo: 0, metros_barrenacion: 0 },
});

export default function PresupuestoView({ openId }) {
  const { user, access } = useAuth();
  const canWrite = (user?.can_write ?? (user?.role !== "consulta"));
  const hasAccess = access?.active ?? true;
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [conceptos, setConceptos] = useState([]);
  const [explosion, setExplosion] = useState(null);
  const [programaCalc, setProgramaCalc] = useState(null);
  const [mineriaCalc, setMineriaCalc] = useState(null);
  const [suministrosCalc, setSuministrosCalc] = useState(null);
  const [dock, setDock] = useState(true);
  const [listQ, setListQ] = useState("");

  const [insumosCat, setInsumosCat] = useState([]);
  const loadList = async () => { const { data } = await api.get("/presupuestos"); setList(data); };
  const loadConceptos = async () => { const { data } = await api.get("/conceptos"); setConceptos(data); };
  const loadInsumos = async () => { const { data } = await api.get("/insumos"); setInsumosCat(data); };
  useEffect(() => { if (!user) return; loadList(); loadConceptos(); loadInsumos(); if (openId) open(openId); /* eslint-disable-next-line */ }, [user]);

  const open = async (id) => {
    const { data } = await api.get(`/presupuestos/${id}`);
    data.programa = data.programa || { fecha_inicio: "", actividades: [] };
    data.mineria = data.mineria || { toneladas: 0, metros_desarrollo: 0, metros_barrenacion: 0 };
    data.moneda_base = data.moneda_base || "MXN";
    data.tipo_cambio = data.tipo_cambio || { USD: 0, EUR: 0 };
    setEditing(data);
    setExplosion(null);
    setProgramaCalc(null);
    setMineriaCalc(null);
    setSuministrosCalc(null);
  };

  // Live totals mirror backend
  const totals = useMemo(() => {
    if (!editing) return null;
    const puMap = Object.fromEntries(conceptos.map((c) => [c.id, c.pu]));
    const insMap = Object.fromEntries(insumosCat.map((i) => [i.id, i]));
    const baseCur = editing.moneda_base || "MXN";
    const tc = editing.tipo_cambio || {};
    const rate = (m) => { if (!m || m === "MXN") return 1; const r = parseFloat(tc[m]); return r > 0 ? r : 1; };
    const conv = (val, m) => (parseFloat(val) || 0) * rate(m) / rate(baseCur);
    let directo = 0;
    const nodes = editing.nodes.map((n) => {
      if (n.tipo === "concepto" || n.tipo === "insumo") {
        const insu = n.tipo === "insumo" ? insMap[n.insumo_id] : null;
        const pu = n.tipo === "insumo" ? (insu ? conv(insu.precio, insu.moneda) : (n.pu ?? 0)) : (puMap[n.concepto_id] ?? n.pu ?? 0);
        const importe = (parseFloat(n.cantidad) || 0) * pu;
        directo += importe;
        return { ...n, pu, importe };
      }
      return n;
    });
    nodes.forEach((n) => { if (n.tipo === "concepto" || n.tipo === "insumo") n.incidencia = directo > 0 ? (n.importe / directo) * 100 : 0; });
    const p = editing.params;
    const ind = directo * (p.indirectos_pct / 100);
    const s1 = directo + ind;
    const fin = s1 * (p.financiamiento_pct / 100);
    const s2 = s1 + fin;
    const uti = s2 * (p.utilidad_pct / 100);
    const s3 = s2 + uti;
    const sfp = s3 * (p.cargos_sfp_pct / 100);
    const total = s3 + sfp;
    return { nodes, directo, ind, fin, uti, sfp, total };
  }, [editing, conceptos, insumosCat]);

  const addPartida = () => setEditing((e) => ({
    ...e, nodes: [...e.nodes, { id: uid(), parent_id: null, tipo: "partida", codigo: String(e.nodes.filter((n) => n.tipo === "partida").length + 1), descripcion: "Nueva partida" }],
  }));
  const addConcepto = (partidaId, c) => setEditing((e) => ({
    ...e, nodes: [...e.nodes, { id: uid(), parent_id: partidaId, tipo: "concepto", codigo: c.codigo, descripcion: c.descripcion, concepto_id: c.id, unidad: c.unidad, cantidad: 1 }],
  }));
  const addInsumo = (partidaId, i) => setEditing((e) => ({
    ...e, nodes: [...e.nodes, { id: uid(), parent_id: partidaId, tipo: "insumo", codigo: i.codigo, descripcion: i.descripcion, insumo_id: i.id, unidad: i.unidad, cantidad: 1 }],
  }));
  const setNode = (id, field, value) => setEditing((e) => ({
    ...e, nodes: e.nodes.map((n) => n.id === id ? { ...n, [field]: field === "cantidad" ? (parseFloat(value) || 0) : value } : n),
  }));
  const removeNode = (id) => setEditing((e) => ({ ...e, nodes: e.nodes.filter((n) => n.id !== id && n.parent_id !== id) }));
  const setParam = (k, v) => setEditing((e) => ({ ...e, params: { ...e.params, [k]: parseFloat(v) || 0 } }));
  const setMonedaBase = (v) => setEditing((e) => ({ ...e, moneda_base: v }));
  const setTC = (k, v) => setEditing((e) => ({ ...e, tipo_cambio: { ...(e.tipo_cambio || {}), [k]: parseFloat(v) || 0 } }));

  const save = async () => {
    try {
      const payload = { nombre: editing.nombre, cliente: editing.cliente, logo: editing.logo || "", logo2: editing.logo2 || "", params: editing.params, moneda_base: editing.moneda_base || "MXN", tipo_cambio: editing.tipo_cambio || { USD: 0, EUR: 0 }, nodes: editing.nodes.map(({ pu, importe, incidencia, subtotal, ...n }) => n), programa: editing.programa || { fecha_inicio: "", actividades: [] }, mineria: editing.mineria || { toneladas: 0, metros_desarrollo: 0, metros_barrenacion: 0 } };
      if (editing.id) await api.put(`/presupuestos/${editing.id}`, payload);
      else { const { data } = await api.post("/presupuestos", payload); setEditing(data); }
      toast.success("Presupuesto guardado");
      loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?`)) return;
    await api.delete(`/presupuestos/${p.id}`);
    toast.success("Presupuesto eliminado");
    if (editing?.id === p.id) setEditing(null);
    loadList();
  };
  const loadExplosion = async () => {
    if (!editing?.id) { toast.error("Guarda el presupuesto primero"); return; }
    const { data } = await api.get(`/presupuestos/${editing.id}/explosion`);
    setExplosion(data);
  };

  const fileRef = useRef();
  const fileRef2 = useRef();
  const onLogo = (file) => {
    const reader = new FileReader();
    reader.onload = () => setEditing((e) => ({ ...e, logo: reader.result }));
    reader.readAsDataURL(file);
  };
  const onLogo2 = (file) => {
    const reader = new FileReader();
    reader.onload = () => setEditing((e) => ({ ...e, logo2: reader.result }));
    reader.readAsDataURL(file);
  };
  const download = async (kind) => {
    if (!editing?.id) { toast.error("Guarda el presupuesto primero"); return; }
    try {
      const res = await api.get(`/presupuestos/${editing.id}/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `presupuesto.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind === "excel" ? "Excel" : "PDF"} generado`);
    } catch (e) { toast.error("No se pudo exportar"); }
  };

  const loadPrograma = async () => {
    if (!editing?.id) { toast.error("Guarda el presupuesto primero"); return; }
    const { data } = await api.get(`/presupuestos/${editing.id}/programa`);
    setProgramaCalc(data);
  };
  const loadMineria = async () => {
    if (!editing?.id) { toast.error("Guarda el presupuesto primero"); return; }
    const { data } = await api.get(`/presupuestos/${editing.id}/mineria`);
    setMineriaCalc(data);
  };
  const recalcPrograma = async () => { await save(); await loadPrograma(); };
  const recalcMineria = async () => { await save(); await loadMineria(); };
  const bloquear = async () => {
    if (!window.confirm("¿Bloquear esta versión? Quedará inalterable como entrega oficial.")) return;
    await api.post(`/presupuestos/${editing.id}/bloquear`);
    toast.success("Presupuesto bloqueado");
    await loadList(); open(editing.id);
  };
  const desbloquear = async () => {
    await api.post(`/presupuestos/${editing.id}/desbloquear`);
    toast.success("Presupuesto desbloqueado");
    await loadList(); open(editing.id);
  };
  const nuevaVersion = async () => {
    const { data } = await api.post(`/presupuestos/${editing.id}/nueva-version`);
    toast.success(`Nueva versión creada: ${data.nombre}`);
    await loadList(); open(data.id);
  };
  const actOf = (nid) => (editing.programa?.actividades || []).find((a) => a.node_id === nid) || { node_id: nid, duracion: 30, inicio: 0, predecesoras: [] };
  const startDate = (offsetDays) => {
    const iso = editing.programa?.fecha_inicio;
    if (!iso) return null;
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + Math.round(offsetDays || 0));
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  };
  const setAct = (nid, field, value) => setEditing((e) => {
    const acts = (e.programa?.actividades || []).map((a) => ({ ...a }));
    let a = acts.find((x) => x.node_id === nid);
    if (!a) { a = { node_id: nid, duracion: 30, inicio: 0, predecesoras: [] }; acts.push(a); }
    if (field === "predecesora") a.predecesoras = value && value !== "none" ? [value] : [];
    else a[field] = parseFloat(value) || 0;
    return { ...e, programa: { ...(e.programa || {}), actividades: acts } };
  });
  const setFechaInicio = (v) => setEditing((e) => ({ ...e, programa: { ...(e.programa || { actividades: [] }), fecha_inicio: v } }));
  const setMineria = (k, v) => setEditing((e) => ({ ...e, mineria: { ...(e.mineria || {}), [k]: parseFloat(v) || 0 } }));
  const conceptoNodes = editing ? editing.nodes.filter((n) => n.tipo === "concepto" || n.tipo === "insumo") : [];
  const loadSuministros = async () => {
    if (!editing?.id) { toast.error("Guarda el presupuesto primero"); return; }
    const { data } = await api.get(`/presupuestos/${editing.id}/suministros`);
    setSuministrosCalc(data);
  };
  const setReal = (mes, val) => setEditing((e) => {
    const arr = (e.programa?.avance_real || []).map((x) => ({ ...x }));
    let a = arr.find((x) => x.mes === mes);
    if (!a) { a = { mes, monto: 0 }; arr.push(a); }
    a.monto = parseFloat(val) || 0;
    return { ...e, programa: { ...(e.programa || {}), avance_real: arr } };
  });
  const realOf = (mes) => (editing?.programa?.avance_real || []).find((x) => x.mes === mes)?.monto ?? 0;
  const rebajes = editing?.mineria?.rebajes || [];
  const setRebaje = (i, field, val) => setEditing((e) => {
    const r = (e.mineria?.rebajes || []).map((x) => ({ ...x }));
    r[i][field] = field === "nombre" ? val : (parseFloat(val) || 0);
    return { ...e, mineria: { ...(e.mineria || {}), rebajes: r } };
  });
  const addRebaje = () => setEditing((e) => ({ ...e, mineria: { ...(e.mineria || {}), rebajes: [...(e.mineria?.rebajes || []), { nombre: "", toneladas: 0, costo: 0 }] } }));
  const removeRebaje = (i) => setEditing((e) => ({ ...e, mineria: { ...(e.mineria || {}), rebajes: (e.mineria?.rebajes || []).filter((_, x) => x !== i) } }));
  const cuList = rebajes.map((r) => (r.toneladas > 0 ? r.costo / r.toneladas : 0));
  const cuMin = Math.min(...(cuList.length ? cuList : [0]));
  const cuMax = Math.max(...(cuList.length ? cuList : [0]));

  const partidas = editing ? editing.nodes.filter((n) => n.tipo === "partida") : [];
  const locked = !!editing?.bloqueado;
  const ew = canWrite && !locked && hasAccess;
  const filteredList = list.filter((p) => !listQ || `${p.nombre} ${p.cliente}`.toLowerCase().includes(listQ.toLowerCase()));

  return (
    <div className="flex flex-1 min-h-0">
      {/* List */}
      <div className="w-72 shrink-0 border-r border-border flex flex-col bg-card">
        <div className="h-12 border-b border-border flex items-center px-3 gap-2">
          <FolderTree className="h-4 w-4 text-primary" />
          <span className="font-head font-bold text-sm uppercase tracking-tight">Presupuestos</span>
          <div className="flex-1" />
          {canWrite && <Button size="icon" className="h-7 w-7 rounded-sm" onClick={() => setEditing(blank())} data-testid="new-presupuesto-btn"><Plus className="h-4 w-4" /></Button>}
        </div>
        <div className="p-2 border-b border-border relative">
          <Search className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={listQ} onChange={(e) => setListQ(e.target.value)} placeholder="Buscar proyecto o cliente…" className="h-7 rounded-sm text-xs pl-7" data-testid="presupuesto-search" />
        </div>
        <div className="overflow-y-auto flex-1">
          {filteredList.map((p) => (
            <button key={p.id} onClick={() => open(p.id)} data-testid={`presupuesto-item-${p.id}`}
              className={`exec-list-item ${editing?.id === p.id ? "active" : ""}`}>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium truncate flex-1">{p.nombre}</span>
                {p.bloqueado && <Lock className="h-3 w-3 text-emerald-600 shrink-0" />}
              </div>
              <div className="text-xs text-muted-foreground truncate">{p.cliente || "—"} · v{p.version || 1}</div>
              <div className="font-mono-num text-xs text-primary mt-0.5">$ {fmt(p.totales?.total, 2)}</div>
            </button>
          ))}
          {filteredList.length === 0 && <div className="p-4 text-xs text-muted-foreground">Sin presupuestos.</div>}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {!editing ? (
          <EmptyState icon={FolderTree} title="Selecciona o crea un presupuesto" text="El presupuesto WBS organiza partidas y conceptos, aplica cargos indirectos, financiamiento, utilidad y SFP, y genera explosión de insumos, programa de obra y reportes."
            action={canWrite && <Button size="sm" className="rounded-sm h-8 gap-1" onClick={() => setEditing(blank())} data-testid="empty-new-presupuesto-btn"><Plus className="h-3.5 w-3.5" /> Nuevo presupuesto</Button>} testId="presupuesto-empty" />
        ) : (
          <>
          <div className="flex-1 min-h-0 overflow-auto p-4 space-y-3">
            {/* Ribbon */}
            <Toolbar testId="presupuesto-toolbar">
              <ToolGroup label="Proyecto">
                <div className="flex gap-2">
                  <div><Label className="text-[9px] uppercase tracking-wider text-muted-foreground">Nombre</Label><Input disabled={!ew} className="mt-0.5 h-8 w-56 text-xs" value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} data-testid="presupuesto-nombre" /></div>
                  <div><Label className="text-[9px] uppercase tracking-wider text-muted-foreground">Cliente</Label><Input disabled={!ew} className="mt-0.5 h-8 w-48 text-xs" value={editing.cliente} onChange={(e) => setEditing({ ...editing, cliente: e.target.value })} data-testid="presupuesto-cliente" /></div>
                </div>
              </ToolGroup>
              <ToolGroup label="Edición">
                {ew && <Button size="sm" className="h-8 rounded-sm gap-1" onClick={save} data-testid="presupuesto-save"><Save className="h-4 w-4" /> Guardar</Button>}
                {ew && <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={addPartida} data-testid="add-partida-btn"><Plus className="h-3.5 w-3.5" /> Partida</Button>}
                {ew && editing.id && <Button size="sm" variant="outline" className="h-8 rounded-sm text-destructive" onClick={() => remove(editing)} data-testid="presupuesto-delete"><Trash2 className="h-4 w-4" /></Button>}
                {!ew && <span className="text-[11px] text-muted-foreground self-center px-1">Solo lectura</span>}
              </ToolGroup>
              <ToolGroup label="Versión">
                {locked && <Badge tone="success" testId="lock-badge"><Lock className="h-3 w-3" /> Oficial v{editing.version || 1}</Badge>}
                {!locked && editing.id && <Badge tone="warning">Borrador v{editing.version || 1}</Badge>}
                {canWrite && editing.id && !locked && <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={bloquear} data-testid="bloquear-btn"><Lock className="h-4 w-4" /> Bloquear</Button>}
                {canWrite && editing.id && locked && <Button size="sm" className="h-8 rounded-sm gap-1" onClick={nuevaVersion} data-testid="nueva-version-btn"><CopyPlus className="h-4 w-4" /> Nueva versión</Button>}
                {user?.role === "admin" && locked && <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={desbloquear} data-testid="desbloquear-btn"><Unlock className="h-4 w-4" /> Desbloquear</Button>}
              </ToolGroup>
              <ToolGroup label="Reportes">
                {editing.logo && <img src={editing.logo} alt="logo" className="h-8 max-w-[80px] object-contain border border-border rounded-sm" data-testid="presupuesto-logo-preview" />}
                {editing.logo2 && <img src={editing.logo2} alt="logo2" className="h-8 max-w-[80px] object-contain border border-border rounded-sm" data-testid="presupuesto-logo2-preview" />}
                {ew && (
                  <>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="logo-file-input" onChange={(e) => e.target.files[0] && onLogo(e.target.files[0])} />
                    <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn"><ImagePlus className="h-4 w-4" /> Logo</Button>
                    <input ref={fileRef2} type="file" accept="image/*" className="hidden" data-testid="logo2-file-input" onChange={(e) => e.target.files[0] && onLogo2(e.target.files[0])} />
                    <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={() => fileRef2.current?.click()} data-testid="upload-logo2-btn"><ImagePlus className="h-4 w-4" /> Logo 2</Button>
                    {editing.logo2 && <Button size="sm" variant="ghost" className="h-8 rounded-sm px-2 text-destructive" onClick={() => setEditing((e) => ({ ...e, logo2: "" }))} data-testid="remove-logo2-btn">×</Button>}
                  </>
                )}
                <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={() => download("excel")} data-testid="export-excel-btn"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
                <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={() => download("pdf")} data-testid="export-pdf-btn"><FileText className="h-4 w-4" /> PDF</Button>
              </ToolGroup>
              <ToolGroup label="Vista">
                <Button size="sm" variant="ghost" className="h-8 rounded-sm gap-1" onClick={() => setDock((d) => !d)} data-testid="toggle-dock-btn">{dock ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />} Propiedades</Button>
              </ToolGroup>
            </Toolbar>

            <Tabs defaultValue="wbs">
              {locked && (
                <div className="mb-3 flex items-center gap-2 text-xs px-3 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" data-testid="lock-banner">
                  <Lock className="h-3.5 w-3.5" /> Esta versión está bloqueada como entrega oficial. Crea una nueva versión para editar.
                </div>
              )}
              <TabsList className="rounded-sm">
                <TabsTrigger value="wbs" className="rounded-sm gap-1" data-testid="tab-wbs"><ListTree className="h-3.5 w-3.5" /> Presupuesto</TabsTrigger>
                <TabsTrigger value="explosion" className="rounded-sm gap-1" onClick={loadExplosion} data-testid="tab-explosion"><PackageOpen className="h-3.5 w-3.5" /> Explosión de insumos</TabsTrigger>
                <TabsTrigger value="programa" className="rounded-sm gap-1" onClick={loadPrograma} data-testid="tab-programa"><GanttChartSquare className="h-3.5 w-3.5" /> Programa de obra</TabsTrigger>
                <TabsTrigger value="mineria" className="rounded-sm gap-1" onClick={loadMineria} data-testid="tab-mineria"><Gauge className="h-3.5 w-3.5" /> Minería</TabsTrigger>
                <TabsTrigger value="suministros" className="rounded-sm gap-1" onClick={loadSuministros} data-testid="tab-suministros"><Truck className="h-3.5 w-3.5" /> Suministros</TabsTrigger>
              </TabsList>

              <TabsContent value="wbs" className="mt-3">
                <div className={`grid gap-4 ${dock ? "lg:grid-cols-4" : "grid-cols-1"}`}>
                  <div className={`${dock ? "lg:col-span-3" : ""} space-y-2`}>
                    <div className="exec-panel overflow-hidden">
                      <table className="qc-table">
                        <thead>
                          <tr>
                            <th style={{ width: 60 }}>Clave</th>
                            <th>Concepto</th>
                            <th style={{ width: 50 }}>Unidad</th>
                            <th style={{ width: 90 }} className="text-right">Cantidad</th>
                            <th style={{ width: 110 }} className="text-right">P.U.</th>
                            <th style={{ width: 120 }} className="text-right">Importe</th>
                            <th style={{ width: 70 }} className="text-right">% Inc.</th>
                            <th style={{ width: 28 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {partidas.map((part) => {
                            const children = (totals?.nodes || editing.nodes).filter((n) => n.parent_id === part.id);
                            const subtotal = children.reduce((s, n) => s + (n.importe || 0), 0);
                            const inc = totals?.directo > 0 ? (subtotal / totals.directo) * 100 : 0;
                            return (
                              <Fragment key={part.id}>
                                <tr className="!bg-primary/5 border-l-2 border-l-primary">
                                  <td className="font-mono-num text-primary font-semibold">{part.codigo}</td>
                                  <td colSpan={4}>
                                    <input disabled={!canWrite} value={part.descripcion} onChange={(e) => setNode(part.id, "descripcion", e.target.value)} className="bg-transparent outline-none font-semibold w-full uppercase text-xs tracking-wide" data-testid={`partida-desc-${part.id}`} />
                                  </td>
                                  <td className="text-right font-mono-num font-semibold" data-testid={`partida-subtotal-${part.id}`}>{fmt(subtotal, 2)}</td>
                                  <td className="text-right font-mono-num text-xs text-muted-foreground">{fmt(inc, 2)}%</td>
                                  <td className="text-center">{ew && <button className="text-destructive" onClick={() => removeNode(part.id)}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                                </tr>
                                {children.map((n) => (
                                  <tr key={n.id} data-testid={`concepto-node-${n.id}`}>
                                    <td className="font-mono-num text-xs text-muted-foreground pl-4 whitespace-nowrap">{n.codigo}</td>
                                    <td className="text-xs">{n.tipo === "insumo" && <Badge tone="primary" className="mr-1.5" testId={`insumo-badge-${n.id}`}>Insumo</Badge>}{n.descripcion}</td>
                                    <td className="font-mono-num text-xs text-muted-foreground">{n.unidad}</td>
                                    <td className="text-right">
                                      <input disabled={!ew} type="number" step="any" value={n.cantidad} onChange={(e) => setNode(n.id, "cantidad", e.target.value)} className="w-full text-right font-mono-num bg-transparent outline-none focus:bg-background focus:border focus:border-primary rounded-sm px-1" data-testid={`node-cant-${n.id}`} />
                                    </td>
                                    <td className="text-right font-mono-num text-xs">{fmt(n.pu, 2)}</td>
                                    <td className="text-right font-mono-num">{fmt(n.importe, 2)}</td>
                                    <td className="text-right font-mono-num text-xs text-muted-foreground">{fmt(n.incidencia, 2)}%</td>
                                    <td className="text-center">{ew && <button className="text-destructive" onClick={() => removeNode(n.id)}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                                  </tr>
                                ))}
                                {ew && (
                                  <tr><td colSpan={8} className="pl-4">
                                    <div className="flex items-center gap-1">
                                      <ConceptoPicker conceptos={conceptos} onPick={(c) => addConcepto(part.id, c)} />
                                      <InsumoDirectoPicker insumos={insumosCat} onPick={(i) => addInsumo(part.id, i)} />
                                    </div>
                                  </td></tr>
                                )}
                              </Fragment>
                            );
                          })}
                          {partidas.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8 text-xs">Agrega una partida desde la barra de herramientas para comenzar.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Properties dock */}
                  {dock && (
                  <div className="space-y-3" data-testid="presupuesto-dock">
                    <Panel title="Moneda y tipo de cambio" tone="primary">
                      <div className="p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <Label className="text-xs text-muted-foreground">Moneda base</Label>
                          <Select value={editing.moneda_base || "MXN"} onValueChange={setMonedaBase} disabled={!ew}>
                            <SelectTrigger className="h-8 w-28 rounded-sm text-xs font-mono-num" data-testid="moneda-base"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="MXN">MXN</SelectItem>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {[["USD", "1 USD = ? MXN"], ["EUR", "1 EUR = ? MXN"]].map(([k, l]) => (
                          <div key={k} className="flex items-center justify-between gap-2">
                            <Label className="text-xs text-muted-foreground">{l}</Label>
                            <Input disabled={!ew} type="number" step="any" value={(editing.tipo_cambio || {})[k] ?? 0} onChange={(e) => setTC(k, e.target.value)} className="w-24 h-8 text-right font-mono-num rounded-sm" data-testid={`tc-${k}`} />
                          </div>
                        ))}
                        <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">Todo se convierte a la moneda base. Captura el TC de los insumos en USD/EUR.</p>
                      </div>
                    </Panel>
                    <Panel title="Cargos sobre costo directo" tone="copper">
                      <div className="p-3 space-y-2">
                        {[["indirectos_pct", "Indirectos %"], ["financiamiento_pct", "Financiamiento %"], ["utilidad_pct", "Utilidad %"], ["cargos_sfp_pct", "Cargos SFP %"]].map(([k, l]) => (
                          <div key={k} className="flex items-center justify-between gap-2">
                            <Label className="text-xs text-muted-foreground">{l}</Label>
                            <Input disabled={!ew} type="number" step="any" value={editing.params[k]} onChange={(e) => setParam(k, e.target.value)} className="w-24 h-8 text-right font-mono-num rounded-sm" data-testid={`param-${k}`} />
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-1 border-t border-border text-[11px]"><span className="text-muted-foreground">Factor de sobrecosto</span><span className="font-mono-num text-copper font-semibold">{fmt(totals?.directo > 0 ? totals.total / totals.directo : 0, 4)}</span></div>
                      </div>
                    </Panel>
                    <Panel title="Resumen" tone="primary">
                      <table className="qc-table">
                        <tbody>
                          <tr><td>Costo directo</td><td className="text-right font-mono-num" data-testid="total-directo">{fmt(totals?.directo, 2)}</td></tr>
                          <tr><td>Indirectos</td><td className="text-right font-mono-num">{fmt(totals?.ind, 2)}</td></tr>
                          <tr><td>Financiamiento</td><td className="text-right font-mono-num">{fmt(totals?.fin, 2)}</td></tr>
                          <tr><td>Utilidad</td><td className="text-right font-mono-num">{fmt(totals?.uti, 2)}</td></tr>
                          <tr><td>Cargos SFP</td><td className="text-right font-mono-num">{fmt(totals?.sfp, 2)}</td></tr>
                          <tr className="!bg-primary/10"><td className="font-semibold">Total</td><td className="text-right font-mono-num font-semibold text-primary" data-testid="total-presupuesto">$ {fmt(totals?.total, 2)}</td></tr>
                        </tbody>
                      </table>
                    </Panel>
                    <Panel title="Información">
                      <div className="p-3 text-[11px] space-y-1 text-muted-foreground">
                        <div className="flex justify-between"><span>Partidas</span><span className="font-mono-num text-foreground">{partidas.length}</span></div>
                        <div className="flex justify-between"><span>Conceptos</span><span className="font-mono-num text-foreground">{conceptoNodes.length}</span></div>
                        <div className="flex justify-between"><span>Versión</span><span className="font-mono-num text-foreground">v{editing.version || 1}</span></div>
                        <div className="flex justify-between"><span>Estado</span><span className="text-foreground">{locked ? "Oficial (bloqueado)" : editing.id ? "Borrador" : "Sin guardar"}</span></div>
                      </div>
                    </Panel>
                  </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="explosion" className="mt-3">
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="qc-table">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>Código</th>
                        <th>Insumo</th>
                        <th style={{ width: 50 }}>Unidad</th>
                        <th style={{ width: 120 }} className="text-right">Cantidad total</th>
                        <th style={{ width: 100 }} className="text-right">P.U.</th>
                        <th style={{ width: 130 }} className="text-right">Importe</th>
                        <th style={{ width: 70 }} className="text-right">% Inc.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {explosion?.insumos?.map((r, i) => (
                        <tr key={i} data-testid={`explosion-row-${r.codigo}`}>
                          <td className="font-mono-num text-primary text-xs">{r.codigo}</td>
                          <td className="text-xs">{r.descripcion}</td>
                          <td className="font-mono-num text-xs text-muted-foreground">{r.unidad}</td>
                          <td className="text-right font-mono-num">{fmt(r.cantidad, 4)}</td>
                          <td className="text-right font-mono-num text-xs">{fmt(r.precio_unitario, 2)}</td>
                          <td className="text-right font-mono-num">{fmt(r.importe, 2)}</td>
                          <td className="text-right font-mono-num text-xs text-muted-foreground">{fmt(r.incidencia, 2)}%</td>
                        </tr>
                      ))}
                      {explosion && explosion.insumos.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-6 text-xs">Sin insumos. Agrega conceptos con matriz.</td></tr>}
                      {!explosion && <tr><td colSpan={7} className="text-center text-muted-foreground py-6 text-xs">Cargando explosión…</td></tr>}
                    </tbody>
                    {explosion?.insumos?.length > 0 && (
                      <tfoot>
                        <tr className="bg-primary/10"><td colSpan={5} className="text-right font-medium">Total insumos</td><td className="text-right font-mono-num font-medium text-primary">$ {fmt(explosion.total, 2)}</td><td></td></tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="programa" className="mt-3">
                <div className="flex items-end gap-3 mb-3 flex-wrap">
                  <div>
                    <Label className="text-[10px]">Fecha de inicio (AAAA-MM-DD)</Label>
                    <Input disabled={!ew} value={editing.programa?.fecha_inicio || ""} onChange={(e) => setFechaInicio(e.target.value)} placeholder="2026-01-05" className="mt-0.5 h-8 w-40 font-mono-num rounded-sm" data-testid="programa-fecha-inicio" />
                  </div>
                  <Button size="sm" className="h-8 rounded-sm gap-1" onClick={recalcPrograma} disabled={!ew} data-testid="programa-recalcular"><GanttChartSquare className="h-4 w-4" /> Guardar y calcular CPM</Button>
                  {programaCalc && <span className="font-mono-num text-xs text-muted-foreground pb-1">Duración total: <span className="text-primary">{fmt(programaCalc.duracion_total, 0)} días</span> · Ruta crítica: {programaCalc.ruta_critica.length} act.</span>}
                </div>

                <div className="border border-border rounded-sm overflow-hidden mb-4">
                  <table className="qc-table">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>Clave</th>
                        <th>Actividad</th>
                        <th style={{ width: 90 }} className="text-right">Duración (d)</th>
                        <th style={{ width: 120 }} className="text-right">Inicio</th>
                        <th style={{ width: 150 }}>Predecesora</th>
                        <th style={{ width: 70 }} className="text-right">Holgura</th>
                        <th style={{ width: 60 }}>Crítica</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conceptoNodes.map((n) => {
                        const a = actOf(n.id);
                        const calc = programaCalc?.actividades?.find((x) => x.node_id === n.id);
                        return (
                          <tr key={n.id} data-testid={`programa-row-${n.id}`} className={calc?.critica ? "bg-destructive/5" : ""}>
                            <td className="font-mono-num text-primary text-xs">{n.codigo}</td>
                            <td className="text-xs">{n.descripcion}</td>
                            <td className="text-right"><input disabled={!ew} type="number" step="any" value={a.duracion} onChange={(e) => setAct(n.id, "duracion", e.target.value)} className="w-full text-right font-mono-num bg-transparent outline-none focus:border focus:border-primary rounded-sm px-1" data-testid={`programa-dur-${n.id}`} /></td>
                            <td className="text-right font-mono-num text-xs" data-testid={`programa-inicio-${n.id}`}>{startDate(calc?.es ?? a.inicio) || `día ${fmt(calc?.es ?? a.inicio ?? 0, 0)}`}</td>
                            <td>
                              <Select value={a.predecesoras?.[0] || "none"} onValueChange={(v) => setAct(n.id, "predecesora", v)} disabled={!ew}>
                                <SelectTrigger className="h-7 rounded-sm text-xs" data-testid={`programa-pred-${n.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— Ninguna</SelectItem>
                                  {conceptoNodes.filter((o) => o.id !== n.id).map((o) => <SelectItem key={o.id} value={o.id} className="text-xs">{o.codigo}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="text-right font-mono-num text-xs">{calc ? fmt(calc.holgura, 1) : "—"}</td>
                            <td>{calc?.critica ? <span className="text-[10px] text-destructive font-medium">RUTA</span> : ""}</td>
                          </tr>
                        );
                      })}
                      {conceptoNodes.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-6 text-xs">Agrega conceptos al presupuesto para programarlos.</td></tr>}
                    </tbody>
                  </table>
                </div>

                {programaCalc && programaCalc.duracion_total > 0 && (
                  <div className="border border-border rounded-sm p-3 mb-4">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Diagrama de Gantt</div>
                    <div className="space-y-1">
                      {programaCalc.actividades.map((a) => (
                        <div key={a.node_id} className="flex items-center gap-2" data-testid={`gantt-bar-${a.node_id}`}>
                          <span className="font-mono-num text-[10px] text-primary w-16 shrink-0">{a.codigo}</span>
                          <div className="flex-1 relative h-5 bg-muted/40 rounded-sm">
                            <div className={`absolute h-5 rounded-sm ${a.critica ? "bg-destructive" : "bg-primary"}`}
                              style={{ left: `${(a.es / programaCalc.duracion_total) * 100}%`, width: `${Math.max((a.duracion / programaCalc.duracion_total) * 100, 1)}%` }}
                              title={`${a.es}–${a.ef} d`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {programaCalc && (
                  <div className="border border-border rounded-sm overflow-hidden">
                    <div className="px-3 h-9 flex items-center border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">Erogaciones mensuales por partida</div>
                    <table className="qc-table">
                      <thead>
                        <tr>
                          <th>Partida</th>
                          {programaCalc.erogaciones.meses.map((m) => <th key={m} className="text-right font-mono-num">{m}</th>)}
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {programaCalc.erogaciones.por_partida.map((p) => (
                          <tr key={p.partida_id} data-testid={`ero-row-${p.partida_id}`}>
                            <td className="text-xs">{p.codigo} · {p.descripcion}</td>
                            {programaCalc.erogaciones.meses.map((m) => <td key={m} className="text-right font-mono-num">{fmt(p.montos[m] || 0, 0)}</td>)}
                            <td className="text-right font-mono-num font-medium">{fmt(p.total, 0)}</td>
                          </tr>
                        ))}
                        <tr className="bg-primary/10">
                          <td className="font-medium">Total mensual</td>
                          {programaCalc.erogaciones.meses.map((m) => <td key={m} className="text-right font-mono-num font-medium text-primary">{fmt(programaCalc.erogaciones.totales_mes[m] || 0, 0)}</td>)}
                          <td className="text-right font-mono-num font-medium text-primary">{fmt(programaCalc.erogaciones.total, 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {programaCalc && programaCalc.curva_s?.meses?.length > 0 && (
                  <div className="border border-border rounded-sm p-3 mt-4 grid lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Curva S · avance acumulado (%)</div>
                      <div style={{ width: "100%", height: 220 }} data-testid="curva-s-chart">
                        <ResponsiveContainer>
                          <LineChart data={programaCalc.curva_s.meses.map((m, i) => ({ mes: m, Planeado: programaCalc.curva_s.planeado_pct[i], Real: programaCalc.curva_s.real_pct[i] }))} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                            <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" domain={[0, 100]} />
                            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="Planeado" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="Real" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Captura de avance real ($/mes)</div>
                      <div className="space-y-1 max-h-52 overflow-auto">
                        {programaCalc.curva_s.meses.map((m) => (
                          <div key={m} className="flex items-center gap-2">
                            <span className="font-mono-num text-xs w-20">{m}</span>
                            <Input disabled={!ew} type="number" step="any" value={realOf(m)} onChange={(e) => setReal(m, e.target.value)} className="h-7 font-mono-num text-right rounded-sm" data-testid={`avance-real-${m}`} />
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2">Guarda y recalcula para actualizar la curva.</p>
                    </div>
                  </div>
                )}

              </TabsContent>

              <TabsContent value="mineria" className="mt-3">
                <div className="flex items-end gap-3 mb-4 flex-wrap">
                  <div><Label className="text-[10px]">Toneladas (producción)</Label><Input disabled={!ew} type="number" step="any" value={editing.mineria?.toneladas || 0} onChange={(e) => setMineria("toneladas", e.target.value)} className="mt-0.5 h-8 w-36 font-mono-num text-right rounded-sm" data-testid="mineria-toneladas" /></div>
                  <div><Label className="text-[10px]">Metros de desarrollo</Label><Input disabled={!ew} type="number" step="any" value={editing.mineria?.metros_desarrollo || 0} onChange={(e) => setMineria("metros_desarrollo", e.target.value)} className="mt-0.5 h-8 w-36 font-mono-num text-right rounded-sm" data-testid="mineria-desarrollo" /></div>
                  <div><Label className="text-[10px]">Metros de barrenación</Label><Input disabled={!ew} type="number" step="any" value={editing.mineria?.metros_barrenacion || 0} onChange={(e) => setMineria("metros_barrenacion", e.target.value)} className="mt-0.5 h-8 w-36 font-mono-num text-right rounded-sm" data-testid="mineria-barrenacion" /></div>
                  <Button size="sm" className="h-8 rounded-sm gap-1" onClick={recalcMineria} disabled={!ew} data-testid="mineria-recalcular"><Gauge className="h-4 w-4" /> Guardar y calcular</Button>
                </div>

                {mineriaCalc && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 max-w-3xl">
                      {[["costo_tonelada", "Costo por tonelada", "$/ton"], ["costo_ml_desarrollo", "Costo por m de desarrollo", "$/ml"], ["costo_ml_barrenacion", "Costo por m de barrenación", "$/ml"]].map(([k, label, u]) => (
                        <div key={k} className="border border-primary/40 rounded-sm p-4 bg-primary/5" data-testid={`kpi-${k}`}>
                          <div className="text-[10px] uppercase tracking-wide text-primary">{label}</div>
                          <div className="font-mono-num text-2xl mt-1">$ {fmt(mineriaCalc.kpis[k], 2)}</div>
                          <div className="font-mono-num text-[10px] text-muted-foreground">{u}</div>
                        </div>
                      ))}
                    </div>
                    <div className="border border-border rounded-sm p-3 max-w-3xl">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Costo por categoría de insumo (sobre ${fmt(mineriaCalc.total, 2)})</div>
                      <div className="space-y-2">
                        {mineriaCalc.por_categoria.map((c) => (
                          <div key={c.tipo} data-testid={`cat-${c.tipo}`}>
                            <div className="flex justify-between text-xs mb-0.5"><span className="capitalize">{c.tipo.replace("_", " ")}</span><span className="font-mono-num">${fmt(c.importe, 2)} · {fmt(c.pct, 1)}%</span></div>
                            <div className="h-2 bg-muted/40 rounded-sm"><div className="h-2 bg-primary rounded-sm" style={{ width: `${c.pct}%` }} /></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div className="border border-border rounded-sm mt-4 max-w-3xl">
                  <div className="px-3 h-9 flex items-center justify-between border-b border-border bg-muted/30">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">KPI por rebaje / frente</span>
                    {ew && <Button size="sm" variant="outline" className="h-7 rounded-sm gap-1" onClick={addRebaje} data-testid="add-rebaje-btn"><Plus className="h-3.5 w-3.5" /> Rebaje</Button>}
                  </div>
                  <table className="qc-table">
                    <thead>
                      <tr>
                        <th>Rebaje / Frente</th>
                        <th style={{ width: 120 }} className="text-right">Toneladas</th>
                        <th style={{ width: 140 }} className="text-right">Costo asignado</th>
                        <th style={{ width: 120 }} className="text-right">Costo/ton</th>
                        <th style={{ width: 32 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rebajes.map((r, i) => {
                        const cu = r.toneladas > 0 ? r.costo / r.toneladas : 0;
                        const cls = cuList.length > 1 && cu === cuMax ? "text-destructive" : cuList.length > 1 && cu === cuMin ? "text-emerald-500" : "";
                        return (
                          <tr key={i} data-testid={`rebaje-row-${i}`}>
                            <td><input disabled={!ew} value={r.nombre} onChange={(e) => setRebaje(i, "nombre", e.target.value)} placeholder="Rebaje N-1" className="bg-transparent outline-none w-full text-xs" data-testid={`rebaje-nombre-${i}`} /></td>
                            <td className="text-right"><input disabled={!ew} type="number" step="any" value={r.toneladas} onChange={(e) => setRebaje(i, "toneladas", e.target.value)} className="w-full text-right font-mono-num bg-transparent outline-none" data-testid={`rebaje-ton-${i}`} /></td>
                            <td className="text-right"><input disabled={!ew} type="number" step="any" value={r.costo} onChange={(e) => setRebaje(i, "costo", e.target.value)} className="w-full text-right font-mono-num bg-transparent outline-none" data-testid={`rebaje-costo-${i}`} /></td>
                            <td className={`text-right font-mono-num font-medium ${cls}`} data-testid={`rebaje-cu-${i}`}>$ {fmt(cu, 2)}</td>
                            <td className="text-center">{ew && <button className="text-destructive" onClick={() => removeRebaje(i)}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                          </tr>
                        );
                      })}
                      {rebajes.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-4 text-xs">Agrega rebajes o frentes para comparar su costo unitario. Guarda para persistir.</td></tr>}
                    </tbody>
                  </table>
                </div>

              </TabsContent>


              <TabsContent value="suministros" className="mt-3">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-muted-foreground">Programa de suministro de insumos por mes (a partir de la explosión y el Gantt).</span>
                  {suministrosCalc && <span className="font-mono-num text-xs text-primary ml-auto">Total: $ {fmt(suministrosCalc.total_importe, 2)}</span>}
                </div>
                <div className="border border-border rounded-sm overflow-auto">
                  <table className="qc-table">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>Código</th>
                        <th>Insumo</th>
                        <th style={{ width: 50 }}>Unidad</th>
                        {suministrosCalc?.meses.map((m) => <th key={m} className="text-right font-mono-num">{m}</th>)}
                        <th style={{ width: 110 }} className="text-right">Cant. total</th>
                        <th style={{ width: 120 }} className="text-right">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suministrosCalc?.insumos.map((i) => (
                        <tr key={i.codigo} data-testid={`suministro-row-${i.codigo}`}>
                          <td className="font-mono-num text-primary text-xs">{i.codigo}</td>
                          <td className="text-xs">{i.descripcion}</td>
                          <td className="font-mono-num text-xs text-muted-foreground">{i.unidad}</td>
                          {suministrosCalc.meses.map((m) => <td key={m} className="text-right font-mono-num">{fmt(i.montos[m] || 0, 2)}</td>)}
                          <td className="text-right font-mono-num font-medium">{fmt(i.cantidad_total, 2)}</td>
                          <td className="text-right font-mono-num">{fmt(i.importe_total, 2)}</td>
                        </tr>
                      ))}
                      {suministrosCalc && suministrosCalc.insumos.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-6 text-xs">Sin insumos. Programa conceptos con matriz.</td></tr>}
                      {!suministrosCalc && <tr><td colSpan={6} className="text-center text-muted-foreground py-6 text-xs">Cargando suministros…</td></tr>}
                    </tbody>
                    {suministrosCalc?.insumos.length > 0 && (
                      <tfoot>
                        <tr className="bg-primary/10">
                          <td colSpan={3} className="text-right font-medium">Importe mensual</td>
                          {suministrosCalc.meses.map((m) => <td key={m} className="text-right font-mono-num font-medium text-primary">{fmt(suministrosCalc.totales_mes_importe[m] || 0, 0)}</td>)}
                          <td></td>
                          <td className="text-right font-mono-num font-medium text-primary">{fmt(suministrosCalc.total_importe, 2)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </TabsContent>

            </Tabs>
          </div>
          <SummaryBar testId="presupuesto-summary-bar" items={[
            { label: "Costo directo", value: `$ ${fmt(totals?.directo, 2)}`, testId: "sb-directo" },
            { label: `Indirectos ${fmt(editing.params.indirectos_pct, 2)}%`, value: fmt(totals?.ind, 2) },
            { label: `Financiamiento ${fmt(editing.params.financiamiento_pct, 2)}%`, value: fmt(totals?.fin, 2) },
            { label: `Utilidad ${fmt(editing.params.utilidad_pct, 2)}%`, value: fmt(totals?.uti, 2) },
            { label: `SFP ${fmt(editing.params.cargos_sfp_pct, 2)}%`, value: fmt(totals?.sfp, 2) },
            { label: "Total presupuesto MXN", value: `$ ${fmt(totals?.total, 2)}`, highlight: true, testId: "sb-total" },
          ]} />
          </>
        )}
      </div>
    </div>
  );
}

function InsumoDirectoPicker({ insumos, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" className="h-6 rounded-sm gap-1 text-xs text-muted-foreground" data-testid="add-insumo-node-btn"><PackageOpen className="h-3.5 w-3.5" /> Agregar insumo directo</Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar insumo…" data-testid="insumo-node-search" />
          <CommandList>
            <CommandEmpty>Sin insumos.</CommandEmpty>
            <CommandGroup>
              {insumos.map((i) => (
                <CommandItem key={i.id} value={`${i.codigo} ${i.descripcion}`} onSelect={() => { onPick(i); setOpen(false); }} data-testid={`insumo-node-pick-${i.codigo}`}>
                  <span className="font-mono-num text-primary text-xs mr-2">{i.codigo}</span>
                  <span className="text-xs flex-1 truncate">{i.descripcion}</span>
                  <span className="text-[9px] px-1 mr-1 bg-muted rounded-sm text-muted-foreground uppercase">{i.familia?.replace("_", " ")}</span>
                  <span className="font-mono-num text-xs text-muted-foreground ml-1">${fmt(i.precio, 2)}/{i.unidad}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ConceptoPicker({ conceptos, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" className="h-6 rounded-sm gap-1 text-xs text-muted-foreground" data-testid="add-concepto-node-btn"><Plus className="h-3.5 w-3.5" /> Agregar concepto</Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar concepto…" />
          <CommandList>
            <CommandEmpty>Sin conceptos.</CommandEmpty>
            <CommandGroup>
              {conceptos.map((c) => (
                <CommandItem key={c.id} value={`${c.codigo} ${c.descripcion}`} onSelect={() => { onPick(c); setOpen(false); }} data-testid={`concepto-pick-${c.codigo}`}>
                  <span className="font-mono-num text-primary text-xs mr-2">{c.codigo}</span>
                  <span className="text-xs flex-1 truncate">{c.descripcion}</span>
                  {c.es_basico && <span className="text-[9px] px-1 mr-1 bg-muted rounded-sm text-muted-foreground uppercase">Básico</span>}
                  <span className="font-mono-num text-xs text-muted-foreground ml-1">${fmt(c.pu, 2)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
